/**
 * Blocking-modal getoond tijdens een lopende JSON/AMEFF-import.
 *
 * Achtergrond (2026-05-26): imports kunnen 10–30s duren bij grote
 * payloads (158 concepts + 153 relations + 22 diagrams = ~5s wall-time
 * op een cold worker; 30+s op een eerste warm-up). Tot Fase 2 was de
 * enige busy-indicatie een onopvallende "…"-suffix op de menu-knop;
 * gebruikers wisten niet of de app vastgelopen was of niet, en klikten
 * regelmatig de import-knop een tweede keer aan met dubbele requests
 * tot gevolg.
 *
 * Deze modal blokkeert de UI met een backdrop zolang `visible=true`
 * en toont een spinner + bilinguale tekst. Caller (RepositoryMenu /
 * PortabilitySettings) bepaalt visible via de bestaande `busy`-state.
 */
import { useTranslation } from "react-i18next";

interface Props {
  /** Toon de modal? Caller bindt aan z'n eigen `busy`-flag. */
  visible: boolean;
  /** Optionele tekst-override, al vertaald (bv. via `t(...)`). Default =
   *  file-reading tekst. Gebruikt voor langlopende acties met andere
   *  semantics (vertalen, ontdubbelen, bulk-update). */
  message?: string;
  /** Oudere bilinguale override; `message` heeft voorrang. */
  messageNl?: string;
  messageEn?: string;
}

export function ImportingModal({ visible, message, messageNl, messageEn }: Props) {
  const { t, i18n } = useTranslation();
  if (!visible) return null;
  const lang: "nl" | "en" = i18n.language === "en" ? "en" : "nl";
  const text = message ?? (lang === "nl" ? messageNl : messageEn) ?? t("importing.readingFile");
  return (
    <div
      className="ledger fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/40"
      // Geen onClick-close: import-progress mag NIET door de gebruiker
      // worden onderbroken (cancel-flow is server-side niet veilig
      // implementeerbaar zonder partial-write-cleanup).
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-3 rounded-md border border-(--ld-control) bg-(--ld-surface) px-5 py-4 shadow-xl">
        <svg
          className="h-5 w-5 animate-spin text-(--ld-accent)"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeOpacity="0.2"
            strokeWidth="3"
          />
          <path
            d="M22 12a10 10 0 0 1-10 10"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
        <span className="text-(--ld-text) text-sm">{text}</span>
      </div>
    </div>
  );
}
